// src/components/common/ProductCard/ProductCard.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFavorites } from '../../../hooks/useFavorites.js';
import { useCart } from '../../../hooks/useCart.js';
import { useToast } from '../../../hooks/useToast.js';
import { Button } from '../../ui/Button/Button.jsx';

export const ProductCard = ({ product, variant = 'default' }) => {
  const [isLiked, setIsLiked] = useState(false);
  const { addToFavorites, removeFromFavorites } = useFavorites();
  const { addToCart } = useCart();
  const { success, error } = useToast();

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (isLiked) {
        await removeFromFavorites(product.id);
        setIsLiked(false);
        success('Удалено из избранного');
      } else {
        await addToFavorites(product.id);
        setIsLiked(true);
        success('Добавлено в избранное');
      }
    } catch (err) {
      error('Не удалось обновить избранное');
    }
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await addToCart({
        product_id: product.id,
        quantity: 1,
      });
      success('Товар добавлен в корзину');
    } catch (err) {
      error('Не удалось добавить в корзину');
    }
  };

  const variants = {
    default: {
      card: { width: '100%' },
      image: { height: '200px' },
      padding: '16px',
    },
    compact: {
      card: { width: '100%', display: 'flex', gap: '16px' },
      image: { width: '120px', height: '120px', flexShrink: 0 },
      padding: '12px',
    },
    grid: {
      card: { width: '100%' },
      image: { height: '180px' },
      padding: '14px',
    },
  };

  const style = variants[variant] || variants.default;

  // Главное фото: помеченное `is_main`, иначе первое по порядку.
  // ⚠️ Логика ровно та же, что в `ProductPage.jsx:300` — карточка и
  //    страница товара ОБЯЗАНЫ показывать одно и то же фото.
  const productImages = product.ProductImages || [];
  const mainImage = productImages.find((img) => img.is_main) || productImages[0];

  return (
    <Link
      to={`/product/${product.id}`}
      style={{
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
      }}
    >
      <div style={{
        backgroundColor: 'rgb(var(--c-surface))',
        border: '1px solid rgb(var(--c-border))',
        borderRadius: '16px',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s, transform 0.2s',
        cursor: 'pointer',
        ...style.card,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.08)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Изображение */}
      <div style={{
        backgroundColor: 'rgb(var(--c-surface-2))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgb(var(--c-text-secondary))',
        fontSize: '12px',
        position: 'relative',
        ...style.image,
        overflow: 'hidden',
      }}>
        {/*
          🔴 ИМЯ ПОЛЯ — `ProductImages`, А НЕ `images`.
          Здесь стояло `product.images?.[0]`, и это был МОЛЧАЛИВЫЙ дефект:
          `productController.js` отдаёт ассоциацию Sequelize под именем
          `ProductImages` (строка 43–44, `as: 'ProductImages'`), а поля
          `images` в ответе API НЕТ ВООБЩЕ. Условие всегда было ложным,
          и карточка показывала «Нет фото» даже у товаров, у которых фото
          в БД ЕСТЬ (проверено: 18 записей в `product_images`).

          ⚠️ Это тот же класс, что «дрейф модели Sequelize»: несовпадение
          имени даёт не 500, а тихий `undefined`. Страница товара
          (`ProductPage.jsx:299`) и админка (`AdminProductForm.jsx:64`)
          читали `ProductImages` правильно — поэтому дефект был виден
          ТОЛЬКО в каталоге и избранном.

          ⚠️ Прежде чем менять имя — сверься с `productController.js`
          (include на строках 43, 123, 306) и с ответом живого API.
        */}
        {mainImage ? (
          <img
            src={mainImage.url}
            alt={mainImage.alt_text || product.name}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            onError={(e) => {
              // ⚠️ Битый URL не должен оставлять пустую рамку: прячем
              // картинку, и тогда ниже проявится подпись «Нет фото».
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement.dataset.imgFailed = '1';
            }}
          />
        ) : (
          'Нет фото'
        )}
        {/* Кнопка избранного */}
        <button
          onClick={handleToggleFavorite}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'rgba(255,255,255,0.9)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            cursor: 'pointer',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {isLiked ? '❤️' : '🤍'}
        </button>
      </div>

      {/* Информация */}
      <div style={{ padding: style.padding }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '8px',
        }}>
          <div>
            <h4 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'rgb(var(--c-text))',
              margin: 0,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {product.name}
            </h4>
            {product.short_description && (
              <p style={{
                fontSize: '13px',
                color: 'rgb(var(--c-text-secondary))',
                margin: '4px 0 0',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {product.short_description}
              </p>
            )}
          </div>
          {product.is_new && (
            <span style={{
              backgroundColor: 'rgb(var(--c-accent))',
              color: 'rgb(var(--c-on-accent))',
              fontSize: '10px',
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: '100px',
              whiteSpace: 'nowrap',
            }}>
              Новинка
            </span>
          )}
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '12px',
        }}>
          <div>
            <span style={{
              fontSize: '18px',
              fontWeight: 700,
              color: 'rgb(var(--c-accent))',
            }}>
              {product.price ? `${product.price} ₽` : 'По запросу'}
            </span>
            {product.old_price && (
              <span style={{
                fontSize: '14px',
                color: 'rgb(var(--c-text-secondary))',
                textDecoration: 'line-through',
                marginLeft: '8px',
              }}>
                {product.old_price} ₽
              </span>
            )}
          </div>
          <Button
            size="sm"
            onClick={handleAddToCart}
            style={{ padding: '6px 12px' }}
          >
            В корзину
          </Button>
        </div>
      </div>
    </div>
    </Link> 
  );
};